import React from 'react';
import { useLocation } from 'react-router-dom';
import { NewPatientForm } from '../../components/PatientForms/forms';
import FollowUpForm from '@components/PatientForms/forms/FollowUpForm';
import { useBypassToken } from '../../hooks/useBypassToken';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import DetailsForm from '@components/PatientForms/forms/DetailsForm';
import { useSelector } from 'react-redux';
import { AuthState } from '../../../store/reducers/auth';
import { IAppState } from '../../../store/reducers';
import WeightManagementForm from '@components/PatientForms/forms/WeightManagementForm';
import WMFollowUpForm from '@components/PatientForms/forms/WMFollowUpForm';

/**
 * Form Page
 *
 * Renders specified form.
 * Handles both standard authentication (via Redux) and
 * token-based authentication (eg, for in-office check-in).
 */
const PatientFormPage: React.FC = () => {
  const location = useLocation();
  const { authUser } = useSelector<IAppState, AuthState>((state) => state.auth);
  const tokenData = useBypassToken();

  // Get formType from URL if not in token data (fallback)
  const searchParams = new URLSearchParams(location.search);
  const queryFormType = searchParams.get('formType');
  const queryAppointmentId = searchParams.get('appointmentId');
  const queryTherapyId = searchParams.get('therapyId');

  // Determine final values
  const patientId = authUser?.id || tokenData.patientId;
  const finalFormType = tokenData.formType || queryFormType;
  const finalAppointmentId = tokenData.appointmentId || queryAppointmentId;
  const finalTherapyId = queryTherapyId;

  if (!patientId) {
    return (
      <Box sx={{ padding: 3 }}>
        <Typography color="error">Patient ID not found. Cannot load form.</Typography>
      </Box>
    );
  }

  if (!finalFormType) {
    return (
      <Box sx={{ padding: 3 }}>
        <Typography color="error">Form type not specified. Cannot load form.</Typography>
      </Box>
    );
  }

  switch (finalFormType) {
    case 'newPatient':
      return <NewPatientForm patientId={patientId} />;
    case 'followUp':
      return <FollowUpForm patientId={patientId} appointmentId={finalAppointmentId} />;
    case 'details':
      return <DetailsForm patientId={patientId} />;
    case 'weightManagement':
      return <WeightManagementForm patientId={patientId} therapyId={finalTherapyId} />;
    case 'wmFollowUp':
      return <WMFollowUpForm patientId={patientId} therapyId={finalTherapyId} appointmentId={finalAppointmentId} />;
    default:
      return (
        <Box sx={{ padding: 3 }}>
          <Typography color="error">Form type "{finalFormType}" not found.</Typography>
        </Box>
      );
  }
};

export default PatientFormPage; 